import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { css, cx } from '@lastbrand/web-ui-styled-system/css'
import { Fragment } from 'react'

import { dsHeroRowFrameLgSeamOwnedClass } from '@src/components/blocks/product-overview-ds/ds-hero-row.styles'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import styles from './product-overview.module.scss'

/**
 * PRODUCT_OVERVIEW — the top-level PDP "above the fold" container (v2 nested). It carries no data of
 * its own; it OWNS the layout and fills the parent page-container width (respecting its margin/
 * padding). It renders its first two children in source order — PRODUCT_GALLERY then PRODUCT_INFO —
 * and ignores any extras. Children are rendered by the page renderer and handed in via
 * `renderedChildren`.
 */
const MAX_OVERVIEW_CHILDREN = 2

// PL owns the nested gallery + info arrangement. Reuse the precompiled shared DS hero frame (single
// source of truth with the non-PL PDP) so Panda emits every desktop frame atomic from its declaring
// module; keep mobile full-width and add the PL-only sticky-stretch chain. The seam-owned variant
// has NO lg bottom padding: on PL the gap below the hero comes from the page-level space-48 seam
// margin on the first divider section (see ds-hero-row.styles.ts).
const overviewDSClass = cx(
  css({
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    width: '100%',
    lg: {
      // Sticky travel chain (same trap the legacy overview documents): the grid stretches the
      // gallery's widget-shell wrapper to the full row height, but the gallery block inside a plain
      // block wrapper would not stretch with it — the shell must be a flex container so the gallery
      // fills the row and its inner `position: sticky` element has room to travel.
      '& > :first-child': { display: 'flex' },
      '& > :first-child > *': { flex: '1 1 auto', minWidth: 0 },
    },
  }),
  dsHeroRowFrameLgSeamOwnedClass,
)

// Gallery widget types valid in the first slot. The DS sticky-stretch chain above targets
// `& > :first-child` POSITIONALLY, so the layout silently depends on the BE nesting a gallery first.
// If a gallery ever arrives as a flat sibling (or second), DS loses the flex stretch that gives the
// gallery's inner `position: sticky` room to travel, and legacy loses its `min-width: 66rem` floor —
// with no error anywhere. `PRODUCT_GALLERY_V2` (SVD) must stay in this set alongside V1.
const OVERVIEW_GALLERY_TYPES: readonly WidgetType[] = [WidgetType.PRODUCT_GALLERY, WidgetType.PRODUCT_GALLERY_V2]

function ProductOverviewWidgetComponent(props: PLTypedWidgetProps<never>) {
  const children = (props.renderedChildren ?? []).slice(0, MAX_OVERVIEW_CHILDREN)
  if (children.length === 0) return null

  // Dev-only tripwire for the positional-slot invariant (ds-parity-pdp task B1). Never throws and never
  // changes render output — a payload shape change is a backend condition the client must still render.
  if (process.env.NODE_ENV !== 'production' && children[0] && !OVERVIEW_GALLERY_TYPES.includes(children[0].type)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[PRODUCT_OVERVIEW] expected a gallery widget in the first slot (the DS sticky-stretch chain is positional), got "${children[0].type}". The hero layout will not stretch correctly.`,
    )
  }

  return (
    <div className={shouldUseDesignSystem() ? overviewDSClass : styles.overview} data-testid="pdp-product-overview">
      {children.map(child => (
        <Fragment key={child.id}>{child.node}</Fragment>
      ))}
    </div>
  )
}

export const ProductOverviewWidget: PLTypedWidgetInterface<never, WidgetType.PRODUCT_OVERVIEW> = {
  id: WidgetType.PRODUCT_OVERVIEW,
  errorBoundary: PLBaseErrorBoundary,
  render: ProductOverviewWidgetComponent,
}
